import {
  AGGREGATE_COUNCIL_SLUG,
  COUNCILS,
  LONDON_COUNCILS,
  DEFAULT_COUNCIL_SLUG,
  councilSlugToShortCode,
  getCouncilConfig,
  isAggregateCouncilScope,
  type CouncilSlug,
} from './councils';

export type PlanningAreaKind =
  | 'country'
  | 'region'
  | 'council'
  | 'place'
  | 'settlement'
  | 'locality'
  | 'community'
  | 'ward'
  | 'postcode-district'
  | 'intermediate-zone'
  | 'data-zone';
export type PlanningAreaFilterMode = 'polygon' | 'postcode_set' | 'radius';
export type PlanningAreaBoundaryQuality = 'official' | 'derived' | 'point_only';

export type PlanningArea = {
  id: string;
  parentId: string | null;
  slug: string;
  name: string;
  displayName: string;
  kind: PlanningAreaKind;
  sourceKind?: PlanningAreaKind | string | null;
  councilSlug: CouncilSlug | null;
  aliases: string[];
  filterMode: PlanningAreaFilterMode;
  boundaryQuality: PlanningAreaBoundaryQuality;
  active: boolean;
  selectable: boolean;
  routeSlugs: string[];
  unavailableReason?: string;
};

export type PlanningAreaRouteSurface = 'updates' | 'map';

export const LOCAL_PLANNING_AREA_KINDS: PlanningAreaKind[] = [
  'place',
  'settlement',
  'locality',
  'community',
  'ward',
  'postcode-district',
  'intermediate-zone',
  'data-zone',
];

export const isLocalPlanningArea = (area: Pick<PlanningArea, 'kind'> | null | undefined) =>
  Boolean(area && LOCAL_PLANNING_AREA_KINDS.includes(area.kind));

type PlanningAreaVisibilityInput = {
  kind?: string | null;
  sourceKind?: string | null;
};

const HIDDEN_SELECTOR_SOURCE_KINDS = new Set(['intermediate-zone']);

export const shouldShowPlanningAreaInSelector = (
  area: PlanningAreaVisibilityInput | null | undefined,
) => {
  if (!area) return false;
  const sourceKind = String(area.sourceKind || area.kind || '').trim().toLowerCase();
  return !HIDDEN_SELECTOR_SOURCE_KINDS.has(sourceKind);
};

const PLACE_FILTER_UNAVAILABLE =
  'Town and village filtering needs backend area membership before it can be exact.';

const normalizeSearchText = (value: string) =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const slugify = (value: string) =>
  normalizeSearchText(value)
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');

const publicCouncilAreas = Object.values(COUNCILS)
  .filter((council) => council.publicEnabled)
  .map<PlanningArea>((council) => ({
    id: council.slug,
    parentId: AGGREGATE_COUNCIL_SLUG,
    slug: council.slug,
    name: council.displayName,
    displayName: council.slug === DEFAULT_COUNCIL_SLUG ? 'Edinburgh' : council.displayName,
    kind: 'council',
    councilSlug: council.slug,
    aliases: [
      council.shortCode,
      council.slug,
      council.slug.replace(/-/g, ' '),
      council.officialName,
    ],
    filterMode: 'polygon',
    boundaryQuality: 'official',
    active: true,
    selectable: true,
    routeSlugs: [council.shortCode],
  }));

const localPlaceSeeds: Array<{
  councilSlug: CouncilSlug;
  name: string;
  kind?: string;
  aliases?: string[];
  boundaryQuality?: PlanningAreaBoundaryQuality;
}> = [
  { councilSlug: 'edinburgh', name: 'Leith', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Portobello', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Stockbridge', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Morningside', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Newington', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Corstorphine', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Gorgie', kind: 'Local area' },
  { councilSlug: 'edinburgh', name: 'Bruntsfield', kind: 'Local area' },
  { councilSlug: 'midlothian', name: 'Dalkeith', kind: 'Town' },
  { councilSlug: 'midlothian', name: 'Bonnyrigg', kind: 'Town' },
  { councilSlug: 'midlothian', name: 'Penicuik', kind: 'Town' },
  { councilSlug: 'midlothian', name: 'Gorebridge', kind: 'Village' },
  { councilSlug: 'midlothian', name: 'Newtongrange', kind: 'Village' },
  { councilSlug: 'midlothian', name: 'Loanhead', kind: 'Town' },
  { councilSlug: 'midlothian', name: 'Mayfield', kind: 'Village' },
  { councilSlug: 'midlothian', name: 'Pathhead', kind: 'Village' },
  { councilSlug: 'midlothian', name: 'Rosewell', kind: 'Village' },
  { councilSlug: 'east-lothian', name: 'Haddington', kind: 'Town' },
  { councilSlug: 'east-lothian', name: 'Musselburgh', kind: 'Town' },
  { councilSlug: 'east-lothian', name: 'North Berwick', kind: 'Town' },
  { councilSlug: 'east-lothian', name: 'Dunbar', kind: 'Town' },
  { councilSlug: 'east-lothian', name: 'Tranent', kind: 'Town' },
  { councilSlug: 'east-lothian', name: 'Prestonpans', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Livingston', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Bathgate', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Linlithgow', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Broxburn', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Armadale', kind: 'Town' },
  { councilSlug: 'west-lothian', name: 'Whitburn', kind: 'Town' },
];

const localPlaceAreas = localPlaceSeeds
  .filter((place) => getCouncilConfig(place.councilSlug)?.publicEnabled)
  .map<PlanningArea>((place) => {
    const council = getCouncilConfig(place.councilSlug);
    const slug = slugify(place.name);
    return {
      id: `${place.councilSlug}:${slug}`,
      parentId: place.councilSlug,
      slug,
      name: place.name,
      displayName: place.name,
      kind: 'place',
      councilSlug: place.councilSlug,
      aliases: [place.name, ...(place.aliases || [])],
      filterMode: 'polygon',
      boundaryQuality: place.boundaryQuality || 'official',
      active: true,
      selectable: false,
      routeSlugs: [council?.shortCode || place.councilSlug, slug],
      unavailableReason: PLACE_FILTER_UNAVAILABLE,
    };
  });

export const SCOTLAND_PLANNING_AREA: PlanningArea = {
  id: AGGREGATE_COUNCIL_SLUG,
  parentId: null,
  slug: AGGREGATE_COUNCIL_SLUG,
  name: 'Scotland',
  displayName: 'All available areas',
  kind: 'country',
  councilSlug: null,
  aliases: ['Scotland', 'All areas', 'All regions'],
  filterMode: 'polygon',
  boundaryQuality: 'official',
  active: true,
  selectable: true,
  routeSlugs: [],
};

export const LONDON_PLANNING_AREA: PlanningArea = {
  id: 'london', parentId: null, slug: 'london', name: 'London', displayName: 'London',
  kind: 'region', councilSlug: null, aliases: ['Greater London'],
  filterMode: 'polygon', boundaryQuality: 'point_only', active: true,
  selectable: true, routeSlugs: ['london'],
};

const londonCouncilAreas = Object.values(LONDON_COUNCILS).map<PlanningArea>((council) => ({
  id: council.slug, parentId: 'london', slug: council.slug,
  name: council.displayName, displayName: council.displayName,
  kind: 'council', councilSlug: council.slug,
  aliases: [council.slug, council.slug.replace(/-/g, ' '), council.officialName],
  // A council query is exact by source identity; we have not published council polygons.
  filterMode: 'polygon', boundaryQuality: 'point_only', active: true,
  selectable: true, routeSlugs: [council.shortCode],
}));

export const PLANNING_AREAS: PlanningArea[] = [
  SCOTLAND_PLANNING_AREA,
  LONDON_PLANNING_AREA,
  ...publicCouncilAreas,
  ...londonCouncilAreas,
  ...localPlaceAreas,
];

export const PLANNING_AREA_ROUTE_PLAN = {
  current: [
    '/planning-updates',
    '/planning-updates/:councilCode',
    '/planning-map',
    '/planning-map/:councilCode',
  ],
  future: [
    '/planning-updates/:councilCode/:placeSlug',
    '/planning-map/:councilCode/:placeSlug',
  ],
  resolution:
    'Resolve councilCode and optional placeSlug to a stable geo_area.id, then pass area_id to feed, map, and count APIs.',
};

export const getPlanningAreaById = (id?: string | null) =>
  PLANNING_AREAS.find((area) => area.id === id) || null;

export const getPlanningAreaByCouncilSlug = (councilSlug?: string | null) => {
  if (councilSlug === 'london') return LONDON_PLANNING_AREA;
  if (isAggregateCouncilScope(councilSlug)) return SCOTLAND_PLANNING_AREA;
  const council = getCouncilConfig(councilSlug);
  return council ? getPlanningAreaById(council.slug) : null;
};

export const getPlanningAreaChildren = (parentId: string = AGGREGATE_COUNCIL_SLUG) =>
  PLANNING_AREAS
    .filter((area) => area.active && area.parentId === parentId && shouldShowPlanningAreaInSelector(area))
    .sort((left, right) => {
      if (left.kind !== right.kind) {
        return left.kind === 'council' ? -1 : 1;
      }
      return left.displayName.localeCompare(right.displayName);
    });

export const getPlanningAreaParentPath = (area: PlanningArea | null) => {
  const path: PlanningArea[] = [];
  let current = area;
  while (current?.parentId) {
    const parent = getPlanningAreaById(current.parentId);
    if (!parent) break;
    path.unshift(parent);
    current = parent;
  }
  return path;
};

export const getPlanningAreaContextLabel = (area: PlanningArea) => {
  if (area.kind === 'country') return 'Country';
  if (area.kind === 'council') return 'Council area';
  const council = area.councilSlug ? getCouncilConfig(area.councilSlug) : null;
  return council ? `${council.displayName}` : 'Local area';
};

export const getPlanningAreaDescriptor = (area: PlanningArea) => {
  if (area.kind === 'region') return 'Region';
  if (area.kind === 'country') return 'Scotland';
  if (area.kind === 'council') return 'Council area';
  const seed = localPlaceSeeds.find((place) => (
    place.councilSlug === area.councilSlug && slugify(place.name) === area.slug
  ));
  return seed?.kind || 'Local area';
};

export const searchPlanningAreas = (query: string, limit = 12) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  const queryParts = normalizedQuery.split(/\s+/).filter(Boolean);

  return PLANNING_AREAS
    .filter((area) => area.active && shouldShowPlanningAreaInSelector(area))
    .map((area) => {
      const council = area.councilSlug ? getCouncilConfig(area.councilSlug) : null;
      const haystack = normalizeSearchText([
        area.displayName,
        area.name,
        area.slug,
        council?.displayName,
        council?.shortCode,
        ...(area.aliases || []),
      ].filter(Boolean).join(' '));
      const exact = haystack === normalizedQuery || area.slug === normalizedQuery;
      const starts = haystack.startsWith(normalizedQuery);
      const containsAll = queryParts.every((part) => haystack.includes(part));
      if (!exact && !starts && !containsAll) return null;
      const score = exact ? 0 : starts ? 1 : area.kind === 'council' ? 2 : 3;
      return { area, score };
    })
    .filter((entry): entry is { area: PlanningArea; score: number } => Boolean(entry))
    .sort((left, right) => (
      left.score - right.score ||
      left.area.kind.localeCompare(right.area.kind) ||
      left.area.displayName.localeCompare(right.area.displayName)
    ))
    .slice(0, limit)
    .map((entry) => entry.area);
};

export const buildPlanningAreaPath = (
  surface: PlanningAreaRouteSurface,
  area: PlanningArea | null,
) => {
  const prefix = surface === 'map' ? '/planning-map' : '/planning-updates';
  if (area?.id === 'london') return `${prefix}/london`;
  if (!area || area.kind === 'country') return prefix;

  const council = area.councilSlug ? getCouncilConfig(area.councilSlug) : null;
  if (!council) return prefix;

  if (isLocalPlanningArea(area)) {
    return `${prefix}/${council.shortCode}/${area.slug}`;
  }

  return `${prefix}/${councilSlugToShortCode(council.slug)}`;
};

export const buildSelectablePlanningAreaPath = (
  surface: PlanningAreaRouteSurface,
  area: PlanningArea | null,
) => {
  if (area && isLocalPlanningArea(area)) {
    const councilArea = area.councilSlug ? getPlanningAreaByCouncilSlug(area.councilSlug) : null;
    return buildPlanningAreaPath(surface, councilArea);
  }
  return buildPlanningAreaPath(surface, area);
};

export const resolvePlanningAreaFromPath = (pathname = '') => {
  const cleanPath = String(pathname || '').split('?')[0].split('#')[0];
  if (cleanPath === '/planning-updates' || cleanPath === '/planning-map') {
    return SCOTLAND_PLANNING_AREA;
  }
  if (!cleanPath.startsWith('/planning-updates/') && !cleanPath.startsWith('/planning-map/')) {
    return null;
  }

  const parts = cleanPath.split('/').filter(Boolean);
  if (parts[1] === 'london') return LONDON_PLANNING_AREA;
  const council = getCouncilConfig(parts[1]);
  if (!council) return getPlanningAreaByCouncilSlug(DEFAULT_COUNCIL_SLUG);

  const placeSlug = parts[2];
  if (placeSlug) {
    return PLANNING_AREAS.find((area) => (
      isLocalPlanningArea(area) &&
      area.councilSlug === council.slug &&
      area.slug === placeSlug
    )) || getPlanningAreaByCouncilSlug(council.slug);
  }

  return getPlanningAreaByCouncilSlug(council.slug);
};

export const serializePlanningArea = (
  area: PlanningArea,
  counts: Partial<Record<CouncilSlug, number>> = {},
) => {
  const council = area.councilSlug ? getCouncilConfig(area.councilSlug) : null;
  const londonCounts = Object.values(LONDON_COUNCILS).map((item) => counts[item.slug]);
  const count = area.id === 'london'
    ? londonCounts.every((value) => Number.isFinite(value))
      ? londonCounts.reduce<number>((total, value) => total + Number(value), 0) : null
    : area.kind === 'country'
    ? Object.values(COUNCILS)
      .filter((item) => item.publicEnabled)
      .reduce((total, item) => total + (Number(counts[item.slug]) || 0), 0)
    : area.kind === 'council' && council
      ? Number(counts[council.slug])
      : null;

  return {
    ...area,
    parentPath: getPlanningAreaParentPath(area).map((parent) => ({
      id: parent.id,
      slug: parent.slug,
      name: parent.displayName,
      kind: parent.kind,
    })),
    contextLabel: getPlanningAreaContextLabel(area),
    descriptor: getPlanningAreaDescriptor(area),
    count: Number.isFinite(count) ? count : null,
  };
};
